const STORAGE_KEY = 'pepper_business_stale_chunk_reload';

// Long enough that a reload has finished and the page is usable, short enough that
// the next real deploy is not shielded by it.
const RELOAD_WINDOW_MS = 30000;

/**
 * @description Whether to recover from a failed chunk request by reloading.
 *
 * A tab open across a deploy asks for an asset filename the new build no longer
 * has, the route fails to mount, and the user is left on a dead page. Reloading
 * fetches the new index and the right filenames.
 *
 * Refuses to reload twice in quick succession: if the asset is genuinely missing
 * rather than renamed, reloading cannot fix it, and a page that reloads forever is
 * worse than an error. In that case the event is left alone, so it throws and gets
 * reported as before.
 * @param now epoch ms
 * @param lastReloadAt epoch ms of the previous recovery, or null
 * @returns {boolean}
 */
export const shouldReloadForStaleChunk = (now, lastReloadAt) =>
  typeof lastReloadAt !== 'number'
  || Number.isNaN(lastReloadAt)
  || now - lastReloadAt > RELOAD_WINDOW_MS;

const readLastReload = (storage) => {
  try {
    return Number.parseInt(storage.getItem(STORAGE_KEY), 10);
  } catch (e) {
    return NaN;
  }
};

const writeLastReload = (storage, now) => {
  try {
    storage.setItem(STORAGE_KEY, String(now));
  } catch (e) {
    // A blocked or full store only costs us the loop guard, not the recovery.
  }
};

/**
 * @description Recover from Vite's `vite:preloadError` by reloading once.
 *
 * The event is cancelable and Vite only throws when it was not prevented, so
 * preventing it is both the recovery and the reason the error stops reaching
 * Bugsnag — where it is a deploy artefact, not a defect.
 * @param win
 * @param storage
 */
export const registerStaleChunkReload = (win = window, storage = win.sessionStorage) => {
  win.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();

    if (!shouldReloadForStaleChunk(now, readLastReload(storage))) {
      return;
    }

    event.preventDefault();
    writeLastReload(storage, now);
    win.location.reload();
  });
};

export default registerStaleChunkReload;
